/**
 * Admin Navigation Component
 *
 * Navigation menu for the admin interface with logout functionality.
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const AdminNav = () => {
  const location = useLocation();
  const { user, logout, isFullAdmin } = useAuth();
  const [appVersion, setAppVersion] = useState('');

  useEffect(() => {
    // Fetch app version from settings API
    const fetchAppVersion = async () => {
      try {
        const response = await fetch('/api/admin/settings', {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.ok) {
          const data = await response.json();
          const versionSetting = data.settings?.find(s => s.settingKey === 'app_version');
          if (versionSetting?.settingValue) {
            setAppVersion(versionSetting.settingValue);
          }
        }
      } catch (error) {
        console.error('Failed to fetch app version:', error);
      }
    };

    fetchAppVersion();

    // Listen for settings update events from AdminSettings
    const handleSettingsUpdate = () => {
      fetchAppVersion();
    };

    window.addEventListener('admin-settings-updated', handleSettingsUpdate);

    return () => {
      window.removeEventListener('admin-settings-updated', handleSettingsUpdate);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
  };

  const allNavItems = [
    { path: '/admin/dashboard', label: 'Dashboard', requiresFullAdmin: false },
    { path: '/admin/reports', label: 'Reports', requiresFullAdmin: false },
    { path: '/admin/dynamic-results', label: 'Dynamic Results', requiresFullAdmin: false },
    { path: '/admin/database', label: 'Database', requiresFullAdmin: true },
    { path: '/admin/settings', label: 'Settings', requiresFullAdmin: true }
  ];

  // Filter nav items based on user role
  const navItems = allNavItems.filter(item =>
    !item.requiresFullAdmin || isFullAdmin
  );

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Navigation Links */}
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <a href="/admin/dashboard" className="flex items-center gap-3">
                <img
                  src={process.env.PUBLIC_URL + '/df-web-logo.svg'}
                  alt="Daring Futures"
                  className="h-12 border border-[#EAEFF5] rounded"
                />
                <span className="text-lg font-bold text-daring-navy min-w-[4rem]">
                  {appVersion || '\u00A0'}
                </span>
              </a>
            </div>
            <div className="hidden sm:ml-8 sm:flex sm:space-x-8">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    location.pathname === item.path
                      ? 'border-daring-teal text-daring-navy'
                      : 'border-transparent text-daring-grey hover:border-daring-light-blue hover:text-daring-navy'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* User Info and Logout */}
          <div className="flex items-center">
            <span className="text-sm text-daring-grey mr-4">
              {user?.firstName} {user?.lastName}
            </span>
            <button
              onClick={handleLogout}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-daring-teal hover:bg-daring-navy focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-daring-teal transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default AdminNav;
